"use client";

import { useEffect, useRef, useState } from "react";

export default function CTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await fetch("/api/request-information", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        console.error("Failed to send email");
      }
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  const STATS = [
    { value: "140+", label: "Researchers in beta" },
    { value: "2.4M", label: "Passages indexed" },
    { value: "97.4%", label: "Citation accuracy" },
    { value: "1.2s", label: "Avg. response time" },
  ];

  return (
    <section
      ref={sectionRef}
      className="cta-section section-pad relative"
      id="security"
    >
      {/* Decorative teal orbs */}
      <div
        className="absolute top-1/4 left-0 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(15,244,198,0.06), transparent)", filter: "blur(40px)" }}
      />
      <div
        className="absolute bottom-1/4 right-0 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,166,35,0.04), transparent)", filter: "blur(60px)" }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          { top: "15%", left: "8%", delay: "0s", size: 4 },
          { top: "70%", left: "12%", delay: "1.5s", size: 3 },
          { top: "30%", right: "10%", delay: "0.8s", size: 5 },
          { top: "80%", right: "20%", delay: "2.2s", size: 3 },
          { top: "50%", left: "50%", delay: "1s", size: 4 },
        ].map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-teal-pulse"
            style={{
              top: p.top,
              left: (p as any).left,
              right: (p as any).right,
              width: p.size,
              height: p.size,
              background: "#0FF4C6",
              opacity: 0.3,
              animationDelay: p.delay,
            }}
          />
        ))}
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
        {/* Status badge */}
        <div className="reveal flex justify-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: "rgba(15,244,198,0.08)", border: "1px solid rgba(15,244,198,0.2)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#0FF4C6] animate-teal-pulse inline-block" />
            <span className="text-[11px] font-semibold text-[#0FF4C6] uppercase tracking-widest">
              IR Agent · Early Access Open
            </span>
          </div>
        </div>

        {/* Headline */}
        <div className="reveal">
          <h2
            className="font-display text-5xl lg:text-7xl font-medium text-white leading-[1.0] tracking-tight mb-6"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Research faster.
            <br />
            <span className="gradient-text-hero">Cite everything.</span>
          </h2>
        </div>

        <div className="reveal" style={{ transitionDelay: "100ms" }}>
          <p className="text-[#8892A4] text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Join 140+ pharma researchers who are already using Myndlayer to cut research time from hours to seconds — with every answer grounded in verifiable citations.
          </p>
        </div>

        {/* Email signup */}
        <div className="reveal" style={{ transitionDelay: "200ms" }}>
          {!submitted ? (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-6">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.name@pharma.com"
                required
                className="flex-1 px-4 py-3 rounded-xl text-sm text-white placeholder-[#4B5568] outline-none focus:ring-2 focus:ring-[#0FF4C6]/40 transition-all"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              />
              <button type="submit" className="btn-teal px-6 py-3 whitespace-nowrap">
                Request Information
              </button>
            </form>
          ) : (
            <div
              className="max-w-md mx-auto mb-6 p-4 rounded-xl flex items-center gap-3"
              style={{ background: "rgba(15,244,198,0.08)", border: "1px solid rgba(15,244,198,0.25)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0FF4C6" strokeWidth="2.5">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
              <p className="text-sm text-[#0FF4C6] font-medium">
                You're on the list! We'll be in touch within 48 hours.
              </p>
            </div>
          )}
          <p className="text-[11px] text-[#4B5568]">
            No credit card required · Pharma-specific onboarding
          </p>
        </div>

        {/* Stats row */}
        <div
          className="mt-16 pt-12 reveal"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)", transitionDelay: "300ms" }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <div
                  className="text-3xl lg:text-4xl font-display font-semibold mb-1"
                  style={{ fontFamily: "Fraunces, serif", color: "#0FF4C6" }}
                >
                  {stat.value}
                </div>
                <div className="text-[11px] text-[#4B5568] uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}